import { integrationApi } from "@repo/api";
import { Button, ModalIsland } from "@saviynt/design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";

interface DeleteIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  integrationId: string | number;
  integrationName?: string;
  onDeleteSuccess?: () => void;
}

export function DeleteIntegrationModal({
  isOpen,
  onClose,
  onConfirm,
  integrationId,
  onDeleteSuccess,
}: DeleteIntegrationModalProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const id =
        typeof integrationId === "string"
          ? parseInt(integrationId, 10)
          : integrationId;
      await integrationApi.deleteIntegration(id);
    },
    onSuccess: () => {
      // Call onDeleteSuccess callback if provided (e.g., to close sidepanel)
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
      // Call onConfirm callback if provided
      if (onConfirm) {
        onConfirm();
      }
    },
    onSettled: () => {
      // Invalidate the integrations query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      // Close modal regardless of success or failure
      onClose();
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-400/50 z-[5]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      {/* Modal */}
      <ModalIsland
        kind="critical"
        alertTitle={intl.formatMessage({
          id: "/Rh1FB",
          defaultMessage: "Are you sure?",
          description: "Delete integration modal title",
        })}
        alertSubtitle={intl.formatMessage({
          id: "dRGvpW",
          defaultMessage:
            "This action will permanently remove the integration and all associated configurations and data traffic. It cannot be undone.",
          description: "Delete integration modal description",
        })}
        onClose={onClose}
        isOpen={isOpen}
        primaryButton={
          <Button
            type="button"
            kind="outlined"
            isEnabled={!deleteMutation.isPending}
            size="medium"
            onClick={handleDelete}
          >
            {intl.formatMessage({
              id: "WodcPq",
              defaultMessage: "Delete",
              description: "Delete button label",
            })}
          </Button>
        }
        secondaryButton={
          <Button type="button" kind="ghost" size="medium" onClick={onClose}>
            {intl.formatMessage({
              id: "0GT0SI",
              defaultMessage: "Cancel",
              description: "Cancel button label",
            })}
          </Button>
        }
        className="z-[6]"
      />
    </>
  );
}
